import { React, useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import "./purchase-record-style.css";
import Loading from "../../dialog/loading/loading";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import UserService from "../../../service/user-service";
import AuthService from "../../../service/auth-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import useIsMobile from "../../../hooks/useIsMobile";

const ITEMS_PER_PAGE = 5;

//timestamp(ms) → "YYYY/MM/DD HH:mm"
const formatTime = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

//訂單狀態中文
const STATUS_LABEL = {
  paid: "已完成",
  pending: "處理中",
  failed: "失敗",
};

const PurchaseRecordCompontent = ({
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const registerFrom = location.pathname;

  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState([]); //累積已拉的訂單 buffer
  const [totalRecords, setTotalRecords] = useState(0); //總訂單數（從 API 拿）
  const [isLoading, setIsLoading] = useState(true);

  //—— Auth gating ——
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  const fetchedRef = useRef(false);

  //初次載入：先 auth gating，通過才拉資料
  useEffect(() => {
    if (fetchedRef.current) return;
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setIsLoading(false);
      setShowLoginDialog(true);
      return;
    }
    if (profile.userValidCode !== "2") {
      setIsLoading(false);
      setShowRegisterPrompt(true);
      return;
    }
    fetchedRef.current = true;
    fetchMore(0, ITEMS_PER_PAGE);
  }, []);

  //補拉訂單（loadmore）— 顯示 loading dialog 至少 1 秒
  //  解構加 fallback：validCode=1 時 server 可能回不含 records 的 shape，防 [...undefined] crash
  const fetchMore = async (skip, limit) => {
    setIsLoading(true);
    const MIN_LOADING_MS = 1000;
    const start = Date.now();

    try {
      const res = await UserService.getPurchaseRecords(skip, limit);
      if (res.data?.status) {
        const { records: newRecords = [], total = 0 } = res.data.data || {};
        setRecords((prev) => [...prev, ...newRecords]);
        setTotalRecords(total);
      }
    } catch (e) {
      console.log(e);
    } finally {
      const remaining = MIN_LOADING_MS - (Date.now() - start);
      if (remaining > 0) {
        await new Promise((r) => setTimeout(r, remaining));
      }
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        } else {
          fetchedRef.current = true;
          fetchMore(0, ITEMS_PER_PAGE);
        }
      }
    } catch (e) {
      console.error("[PurchaseRecord] firebase-login 失敗:", e);
    }
  };

  //點到指定頁，缺資料就補拉，最後滾回頁面最上方
  const goToPage = async (targetPage) => {
    const needed = targetPage * ITEMS_PER_PAGE;
    if (records.length < needed && records.length < totalRecords) {
      await fetchMore(records.length, needed - records.length);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(targetPage);
  };

  //計算頁籤陣列
  const calcPages = (currentPage, total, windowSize = 3) => {
    if (!Number.isFinite(total) || total <= 0) return [];

    const safeTotal = Math.floor(total);
    const half = Math.floor(windowSize / 2);

    // 把 currentPage 夾在 1~total
    const p = Math.min(Math.max(1, Math.floor(currentPage)), safeTotal);

    // 先以「置中」估算
    let start = p - half;
    let end = p + half;

    // 左邊超出就往右補
    if (start < 1) {
      end += 1 - start;
      start = 1;
    }

    // 右邊超出就往左補
    if (end > safeTotal) {
      start -= end - safeTotal;
      end = safeTotal;
      if (start < 1) start = 1;
    }

    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const total = Math.ceil(totalRecords / ITEMS_PER_PAGE); //總頁數
  const pagedData = records.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  let pageArray = calcPages(currentPage, total);

  return (
    <div className="purchase-record-page">
      {/* API 載入中（至少顯示 1 秒）*/}
      {isLoading && <Loading />}

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="title">
            <img src="../images/ic-records.png"></img>
            <span>購買記錄</span>
          </div>

          {total > 0 && (
            <div className="records">
              {pagedData.map((order) => {
                return (
                  <div className="item-record" key={order.orderID}>
                    <div className="order-status">
                      <span>{STATUS_LABEL[order.status] || order.status}</span>
                      <span className="number">共 1 項商品</span>
                    </div>

                    <div className="order-line"></div>

                    <div className="order-info">
                      <div className="item-info">
                        <span className="item-title">訂單編號</span>
                        <span>{order.orderID}</span>
                      </div>
                      <div className="item-info">
                        <span className="item-title">建立時間</span>
                        <span>{formatTime(order.createdAt)}</span>
                      </div>
                      <div className="item-info">
                        <span className="item-title">付款總額</span>
                        <span className="order-amout">{`NT$${order.amount}`}</span>
                      </div>
                    </div>

                    <NavLink
                      className="nav-link"
                      to={`/purchase-detail?order=${encodeURIComponent(
                        order.orderID,
                      )}`}
                    >
                      <span className="check">查看明細</span>
                    </NavLink>
                  </div>
                );
              })}
            </div>
          )}

          {total > 0 && (
            <div className="purchase-selector">
              <img
                src={
                  currentPage - 1 == 0
                    ? "../images/ic-non-select-left.png"
                    : "../images/ic-select-left.png"
                }
                onClick={() => {
                  if (currentPage - 1 == 0) return;
                  goToPage(currentPage - 1);
                }}
              ></img>
              <div
                className={`pages ${total == 1 ? "one-page" : total == 2 ? "two-page" : ""}`}
              >
                {pageArray.map((number) => (
                  <span
                    key={number}
                    className={number == currentPage ? "current-page" : ""}
                    onClick={() => {
                      if (number == currentPage) return;
                      goToPage(number);
                    }}
                  >
                    {number}
                  </span>
                ))}
              </div>
              <img
                src={
                  currentPage == total
                    ? "../images/ic-non-select-right.png"
                    : "../images/ic-select-right.png"
                }
                onClick={() => {
                  if (currentPage == total) return;
                  goToPage(currentPage + 1);
                }}
              ></img>
            </div>
          )}

          {total == 0 && <div className="no-record">尚無紀錄</div>}

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {isMobile && (
        <div className="mobile-main">
          <div className="m-content">
            <div className="m-title">
              <img src="../images/ic-records.png" />
              <span>購買記錄</span>
            </div>

            {total > 0 && (
              <div className="m-records">
                {pagedData.map((order) => (
                  <div className="m-item-record" key={order.orderID}>
                    <div className="m-order-status">
                      <span>{STATUS_LABEL[order.status] || order.status}</span>
                      <span className="m-number">共 1 項商品</span>
                    </div>

                    <div className="m-order-line"></div>

                    <div className="m-order-info">
                      <div className="m-item-info">
                        <span className="m-item-title">訂單編號</span>
                        <span>{order.orderID}</span>
                      </div>
                      <div className="m-item-info">
                        <span className="m-item-title">建立時間</span>
                        <span>{formatTime(order.createdAt)}</span>
                      </div>
                      <div className="m-item-info">
                        <span className="m-item-title">付款總額</span>
                        <span className="m-order-amount">{`NT$${order.amount}`}</span>
                      </div>
                    </div>

                    <NavLink
                      className="nav-link"
                      to={`/purchase-detail?order=${encodeURIComponent(
                        order.orderID,
                      )}`}
                    >
                      <span className="m-check">查看明細</span>
                    </NavLink>
                  </div>
                ))}
              </div>
            )}

            {total > 0 && (
              <div className="m-purchase-selector">
                <img
                  src={
                    currentPage - 1 === 0
                      ? "../images/ic-non-select-left.png"
                      : "../images/ic-select-left.png"
                  }
                  onClick={() => {
                    if (currentPage - 1 !== 0) goToPage(currentPage - 1);
                  }}
                />
                <div
                  className={`m-pages ${total === 1 ? "one-page" : total === 2 ? "two-page" : ""}`}
                >
                  {pageArray.map((number) => (
                    <span
                      key={number}
                      className={number === currentPage ? "current-page" : ""}
                      onClick={() => {
                        if (number !== currentPage) goToPage(number);
                      }}
                    >
                      {number}
                    </span>
                  ))}
                </div>
                <img
                  src={
                    currentPage === total
                      ? "../images/ic-non-select-right.png"
                      : "../images/ic-select-right.png"
                  }
                  onClick={() => {
                    if (currentPage !== total) goToPage(currentPage + 1);
                  }}
                />
              </div>
            )}

            {total === 0 && <div className="m-no-record">尚無紀錄</div>}
          </div>

          <SiteFooter />
        </div>
      )}

      {/* 未登入訪客；關掉 → 留在當前頁（本頁未登入無內容）*/}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="購買記錄需要完成註冊才能查看，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default PurchaseRecordCompontent;
